import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ChoiceGroup, ConsentChecks, DonePanel, Field, GoldButton, ToolShell,
  fmt, inputCls, parseNum, selectCls,
} from "@/components/FormUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL = "Simulación orientativa basada en parámetros habituales de financiación de adquisiciones en España y en los datos introducidos por el usuario. La concesión y las condiciones de la financiación dependen de cada entidad. No constituye una oferta de financiación ni de compra.";

const SECTORES = [
  "Industrial y manufactura", "Distribución y comercio mayorista", "Construcción",
  "Ingeniería y servicios técnicos", "Servicios profesionales", "Tecnología y software",
  "Salud y servicios sanitarios", "Transporte y logística", "Alimentación y bebidas",
  "Hostelería y restauración", "Comercio minorista", "Medioambiente y gestión de residuos",
  "Alquiler de maquinaria y equipos", "Educación y formación", "Otros",
];

const PERFILES = [
  { v: "empresario", l: "Empresario que quiere crecer" },
  { v: "inversor", l: "Inversor particular" },
  { v: "mbi-mbo", l: "Directivo (MBI-MBO)" },
  { v: "family_office", l: "Family office" },
  { v: "otro", l: "Otro" },
];

export default function Compra() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    sector: SECTORES[0], ebitda: "", capex: "", capex_unknown: false, var_circulante: "",
    fondos_propios: "", vendor_pct: "0", plazo: "7",
  });
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({
    nombre: "", email: "", telefono: "", perfil_comprador: "", objetivo_identificado: "",
    sectores_interes: "", zona_interes: "", consent_privacidad: false, consent_comercial: false,
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  useEffect(() => {
    document.title = "¿Cuánto puedo pagar por una empresa? | AJG M&A Consulting";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const payload = () => ({
    sector: form.sector,
    ebitda: parseNum(form.ebitda),
    capex: form.capex_unknown ? null : (form.capex ? parseNum(form.capex) : null),
    var_circulante: form.var_circulante ? parseNum(form.var_circulante) : null,
    fondos_propios: parseNum(form.fondos_propios),
    vendor_pct: parseNum(form.vendor_pct),
    plazo: parseInt(form.plazo, 10),
  });

  const analyze = async (e) => {
    e.preventDefault();
    const p = payload();
    if (!(p.ebitda > 0) || Number.isNaN(p.fondos_propios)) {
      toast.error("Completa el EBITDA y tus fondos propios.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/compra/analyze`, p);
      setResult(data);
      setDone(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo calcular.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad || !lead.perfil_comprador || !lead.objetivo_identificado) {
      toast.error("Completa todos los campos y acepta la política de privacidad.");
      return;
    }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/compra/lead`, {
        datos: payload(),
        nombre: lead.nombre, email: lead.email, telefono: lead.telefono,
        perfil_comprador: lead.perfil_comprador, objetivo_identificado: lead.objetivo_identificado,
        sectores_interes: lead.sectores_interes, zona_interes: lead.zona_interes,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLeadLoading(false);
    }
  };

  const goServicios = () => {
    navigate("/");
    setTimeout(() => document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" }), 450);
  };

  const e = result?.estructura;

  return (
    <ToolShell
      eyebrow="Herramientas · Capacidad de compra"
      title="¿Cuánto puedo pagar por una empresa?"
      intro="Calcula el precio máximo que puedes financiar de forma prudente según la caja que genera la empresa objetivo y tus fondos propios."
    >
      <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-start">
        <form onSubmit={analyze} className="space-y-6" data-testid="compra-form">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Empresa objetivo</p>
          <Field label="Sector">
            <select data-testid="compra-sector" value={form.sector} onChange={set("sector")} className={selectCls}>
              {SECTORES.map((s) => <option key={s} value={s} className="bg-ink-2 text-white">{s}</option>)}
            </select>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="EBITDA anual normalizado (€)">
              <input data-testid="compra-ebitda" value={form.ebitda} onChange={set("ebitda")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Capex anual de mantenimiento (€)">
              <div className="space-y-2">
                <input
                  data-testid="compra-capex"
                  value={form.capex}
                  onChange={set("capex")}
                  inputMode="numeric"
                  disabled={form.capex_unknown}
                  className={`${inputCls} disabled:opacity-40`}
                />
                <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-400">
                  <input type="checkbox" checked={form.capex_unknown} onChange={(e) => setForm({ ...form, capex_unknown: e.target.checked, capex: "" })} className="h-4 w-4 accent-gold" data-testid="compra-capex-unknown" />
                  No lo sé (se estima el 15 % del EBITDA)
                </label>
              </div>
            </Field>
          </div>
          <Field label="Variación anual prevista del circulante (€)" hint="Opcional, por defecto 0. Positivo si necesitas más circulante.">
            <input data-testid="compra-varc" value={form.var_circulante} onChange={set("var_circulante")} inputMode="numeric" className={inputCls} />
          </Field>
          <p className="pt-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Tú, como comprador</p>
          <Field label="Fondos propios disponibles para la operación (€)">
            <input data-testid="compra-fp" value={form.fondos_propios} onChange={set("fondos_propios")} inputMode="numeric" className={inputCls} />
          </Field>
          <Field label="¿Aceptaría el vendedor aplazar parte del precio (vendor loan)?">
            <ChoiceGroup
              testid="compra-vendor"
              options={[{ v: "0", l: "No" }, { v: "10", l: "Sí, hasta un 10 %" }, { v: "20", l: "Sí, hasta un 20 %" }, { v: "30", l: "Sí, hasta un 30 %" }]}
              value={form.vendor_pct}
              onChange={(v) => setForm({ ...form, vendor_pct: v })}
            />
          </Field>
          <Field label="Plazo de la financiación bancaria">
            <ChoiceGroup
              testid="compra-plazo"
              options={[{ v: "5", l: "5 años" }, { v: "6", l: "6 años" }, { v: "7", l: "7 años" }]}
              value={form.plazo}
              onChange={(v) => setForm({ ...form, plazo: v })}
            />
          </Field>
          <GoldButton loading={loading} testid="compra-submit">Calcular mi capacidad de compra</GoldButton>
          <p className="text-xs leading-relaxed text-slate-500">{LEGAL}</p>
        </form>

        <div className="lg:sticky lg:top-10">
          {result ? (
            result.aplicable ? (
              <div className="relative border border-gold/20 bg-ink-3/60 p-8" data-testid="compra-result">
                <div className="pointer-events-none absolute -left-2 -top-2 h-5 w-5 border-l border-t border-gold" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Precio máximo financiable (rango prudente)</p>
                <p className="mt-4 font-serif text-4xl leading-tight text-white sm:text-5xl" data-testid="compra-precio">
                  {fmt(result.precio_min)} € <span className="italic text-gold">–</span> {fmt(result.precio_max)} €
                </p>

                <div className="mt-8">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Estructura de financiación</p>
                  <div className="flex h-9 w-full overflow-hidden" data-testid="compra-chart">
                    <div className="bg-gold" style={{ width: `${(e.fp / e.precio) * 100}%` }} />
                    <div className="bg-gold/45" style={{ width: `${(e.deuda / e.precio) * 100}%` }} />
                    <div className="bg-slate-500/50" style={{ width: `${(e.vendor / e.precio) * 100}%` }} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                    <span><span className="mr-1.5 inline-block h-2.5 w-2.5 bg-gold" />Fondos propios: {fmt(e.fp)} €</span>
                    <span><span className="mr-1.5 inline-block h-2.5 w-2.5 bg-gold/45" />Deuda bancaria: {fmt(e.deuda)} €</span>
                    {e.vendor > 0 && <span><span className="mr-1.5 inline-block h-2.5 w-2.5 bg-slate-500/50" />Vendor loan: {fmt(e.vendor)} €</span>}
                  </div>
                </div>

                <div className="mt-8 space-y-4 border-t border-gold/10 pt-6">
                  <p className="text-sm text-slate-300" data-testid="compra-multiplo">
                    Múltiplo implícito: <strong className="text-white">{String(result.multiplo).replace(".", ",")}x EBITDA</strong>
                  </p>
                  <p className="border-l-2 border-gold/50 pl-4 text-sm leading-relaxed text-slate-300" data-testid="compra-mensaje">
                    {result.mensaje_sector}
                  </p>
                  <p className="text-sm leading-relaxed text-slate-400" data-testid="compra-restriccion">
                    {result.restriccion_txt}
                  </p>
                  {result.capex_auto && (
                    <p className="text-xs text-slate-500">Capex estimado automáticamente: {fmt(result.capex)} € (15 % del EBITDA).</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="compra-notaplicable">
                <p className="font-serif text-2xl leading-snug text-white">{result.mensaje}</p>
              </div>
            )
          ) : (
            <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="compra-empty">
              <p className="text-sm leading-relaxed text-slate-400">
                Introduce los datos de la empresa objetivo y tu capacidad financiera para conocer el precio máximo prudente.
              </p>
            </div>
          )}
        </div>
      </div>

      {result?.aplicable && (
        <div className="mt-20 border-t border-gold/10 pt-16">
          <div className="mx-auto max-w-2xl">
            {done ? (
              <DonePanel
                testid="compra-done"
                title="Hemos enviado el análisis a tu email."
                subtitle="Un socio de AJG te contactará en 24-48 h para comentar la estructura óptima de forma confidencial."
                extra={
                  <button onClick={goServicios} data-testid="compra-servicios-link" className="mt-8 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all hover:bg-gold hover:text-ink">
                    Si buscas empresas a la venta, podemos ayudarte a identificarlas y negociar la operación
                  </button>
                }
              />
            ) : (
              <form onSubmit={submitLead} className="space-y-5" data-testid="compra-lead-form">
                <h2 className="font-serif text-3xl text-white">Recibe tu análisis de capacidad de compra</h2>
                <p className="text-sm leading-relaxed text-slate-300">
                  Estructura de financiación detallada, cuadro de amortización, DSCR año a año y análisis de sensibilidad en PDF.
                </p>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Nombre">
                    <input data-testid="compra-nombre" value={lead.nombre} onChange={(e) => setLead({ ...lead, nombre: e.target.value })} required className={inputCls} />
                  </Field>
                  <Field label="Teléfono">
                    <input data-testid="compra-telefono" type="tel" value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} required className={inputCls} />
                  </Field>
                </div>
                <Field label="Email">
                  <input data-testid="compra-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className={inputCls} />
                </Field>
                <Field label="Tu perfil">
                  <select data-testid="compra-perfil" value={lead.perfil_comprador} onChange={(e) => setLead({ ...lead, perfil_comprador: e.target.value })} required className={selectCls}>
                    <option value="" disabled className="bg-ink-2">Selecciona una opción</option>
                    {PERFILES.map((p) => <option key={p.v} value={p.v} className="bg-ink-2 text-white">{p.l}</option>)}
                  </select>
                </Field>
                <Field label="¿Tienes identificada una empresa objetivo?">
                  <ChoiceGroup
                    testid="compra-objetivo"
                    options={[{ v: "si", l: "Sí" }, { v: "no", l: "No" }]}
                    value={lead.objetivo_identificado}
                    onChange={(v) => setLead({ ...lead, objetivo_identificado: v })}
                  />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Sectores de interés" hint="Opcional">
                    <input data-testid="compra-sectores" value={lead.sectores_interes} onChange={(e) => setLead({ ...lead, sectores_interes: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label="Zona geográfica de interés" hint="Opcional">
                    <input data-testid="compra-zona" value={lead.zona_interes} onChange={(e) => setLead({ ...lead, zona_interes: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                <ConsentChecks lead={lead} setLead={setLead} prefix="compra" />
                <GoldButton loading={leadLoading} testid="compra-lead-submit">Recibir mi análisis de capacidad de compra</GoldButton>
              </form>
            )}
          </div>
        </div>
      )}
    </ToolShell>
  );
}
